import type { SxProps, Theme } from "@mui/material";
import { Stack } from "@mui/material";
import type { PropsWithChildren, Ref } from "react";
import { memo, useCallback, useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { CurrentTileContextProvider } from "../contexts/CurrentTileContextProvider";
import { useDashboardContext } from "../contexts/DashboardContext";
import type { TileObject } from "../models/TileObject";

interface WidgetContainerProps extends PropsWithChildren<React.HTMLAttributes<HTMLDivElement>> {
  ref?: Ref<HTMLDivElement>;
  tile: TileObject;
  sx?: SxProps<Theme>;
}

const TileComponent = ({ ref, tile, children, ...props }: WidgetContainerProps) => {
  const [hover, setHover] = useState(false);
  const hoverOver = useCallback(() => setHover(true), []);
  const hoverOut = useCallback(() => setHover(false), []);
  const { selectedWidget, setSelectedWidgetId } = useDashboardContext();
  const isSelected = selectedWidget?.widgetId === tile.item.widget.widgetId;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setSelectedWidgetId(tile.item.widget.widgetId);
    },
    [tile.item.widget, setSelectedWidgetId]
  );

  return (
    <Stack
      ref={ref}
      {...props}
      sx={{
        border: "1px solid",
        borderColor: isSelected ? "primary.main" : "divider",
        borderRadius: 2,
        p: 1.5,
        backgroundColor: "background.paper",
        transition: "box-shadow linear .04s, border-color linear .2s !important",
        "&:hover": {
          borderColor: isSelected ? "primary.main" : "text.disabled",
        },

        ...props.sx,
      }}
      onClick={handleClick}
      onMouseEnter={hoverOver}
      onMouseLeave={hoverOut}
    >
      <CurrentTileContextProvider tile={tile} hover={hover}>
        {tile.isInitialized ? children : <InlineLoader text="Initializing..." />}
      </CurrentTileContextProvider>
    </Stack>
  );
};

export default memo(TileComponent);
